import React from 'react';
import { CheckCircle, Target, Users, Award } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';

const About = () => {
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);
  
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative h-96 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80"
            alt={t('aboutTitle')}
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 60%' }}
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="text-white">
            <h1 className="text-5xl font-bold mb-4">{t('aboutTitle')}</h1>
            <p className="text-xl">{t('aboutSubtitle')}</p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('aboutTitle')}</h2>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                {t('aboutContent1')}
              </p>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                {t('aboutContent2')}
              </p>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                {t('aboutContent3')}
              </p>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                {t('aboutContent4')}
              </p>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                {t('aboutContent5')}
              </p>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                {t('aboutContent6')}
              </p>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                {t('aboutContent7')}
              </p>
              
              <p className="text-gray-700 leading-relaxed mb-6">
                {t('aboutContent8')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">{t('ourValues')}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-sky-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('quality')}</h3>
              <p className="text-gray-600">{language === 'tr' ? 'Satış sonrası hizmet kalitesini sürekli yükseltiyoruz' : language === 'en' ? 'Continuously improving after-sales service quality' : language === 'de' ? 'Kontinuierliche Verbesserung der Servicequalität' : 'تحسين مستمر لجودة خدمة ما بعد البيع'}</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-sky-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('efficiency')}</h3>
              <p className="text-gray-600">{language === 'tr' ? 'Operasyonel verimliliği artırarak değer yaratıyoruz' : language === 'en' ? 'Creating value by increasing operational efficiency' : language === 'de' ? 'Wertschöpfung durch Steigerung der Betriebseffizienz' : 'خلق قيمة من خلال زيادة الكفاءة التشغيلية'}</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-sky-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('customerFocus')}</h3>
              <p className="text-gray-600">{language === 'tr' ? 'Müşteri memnuniyetini her zaman merkeze alıyoruz' : language === 'en' ? 'Always putting customer satisfaction at the center' : language === 'de' ? 'Kundenzufriedenheit immer im Mittelpunkt' : 'رضا العملاء دائمًا في المركز'}</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-sky-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('sustainability')}</h3>
              <p className="text-gray-600">{language === 'tr' ? 'Çevreye duyarlı ve sürdürülebilir çözümler sunuyoruz' : language === 'en' ? 'Offering environmentally friendly and sustainable solutions' : language === 'de' ? 'Umweltfreundliche und nachhaltige Lösungen anbieten' : 'تقديم حلول صديقة للبيئة ومستدامة'}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">{t('partnershipTitle')}</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                {t('partnershipContent1')}
              </p>
              <p className="text-gray-700 leading-relaxed">
                {t('partnershipContent2')}
              </p>
            </div>
            <div className="relative h-96 rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://customer-assets.emergentagent.com/job_elerien-tr/artifacts/ykw6hnje_tu%CC%88m%20makineler%20studio.jpg"
                alt="Elerein Makineleri"
                className="w-full h-full object-cover"
                style={{ objectPosition: 'center 70%' }}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
